import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { type ActionDeps } from "./types";
import { durationToUnitsSeconds, parseEntryId } from "./entryId";

export type AddPayload = {
	id: number;
	date: string;
	matter: string;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	activity: string;
	end_time: string;
	shouldExport?: boolean;
};

/** Map the form payload to the v2 wire shape. Empty strings become null. */
export function toCreateBody(entry: AddPayload) {
	return {
		date: entry.date,
		unitsSeconds: durationToUnitsSeconds(entry.duration),
		description: entry.description,
		matterId: entry.matter_id || null,
		taskId: entry.task || null,
		activityId: entry.activity || null,
		endTime: entry.end_time || null,
	};
}

export function addAction(
	entry: AddPayload,
	deps: ActionDeps,
	onSuccess?: () => void,
	onEntryFailure?: (id: number, error: string) => void,
): QueuedAction {
	const { shouldExport } = entry;
	return createAction({
		execute: async () => {
			const resp = await deps.makeApiRequest({
				endpoint: "/v2/entries",
				method: "POST",
				body: toCreateBody(entry),
			});
			if (resp.status === "ERROR") throw new Error(resp.error || "Request failed");
			if (shouldExport) {
				if (!resp.data?.id) throw Object.assign(new Error("No entry ID returned"), { phase: "export" });
				// /records/exportRecords (v1) still takes numeric ids — strip the
				// "ent_" prefix here so the rest of the queue doesn't have to
				// learn about prefixed ids. The entry already persisted by this
				// point, so any failure here is an *export* failure — tag the
				// thrown error so the toast handler shows the right message
				// instead of "Failed to add entry" (which would tempt the user
				// to retry and create a duplicate).
				let entryId: number;
				try {
					entryId = parseEntryId(resp.data.id);
				} catch (err) {
					const wrapped = err instanceof Error ? err : new Error("Malformed entry id");
					throw Object.assign(wrapped, { phase: "export" });
				}
				const exportResp = await deps.makeApiRequest({
					endpoint: "/records/exportRecords",
					method: "POST",
					body: { ids: [entryId] },
				});
				if (exportResp.status === "ERROR") {
					const msg = exportResp.error || "Export failed";
					onEntryFailure?.(entryId, msg);
					throw Object.assign(new Error(msg), { phase: "export" });
				}
			}
		},
		rollback: null,
		toast: {
			loading: "Adding entry…",
			success: shouldExport ? "Entry added and exported" : "Entry added",
			error: shouldExport
				? (err: unknown) => (err as any)?.phase === "export" ? "Entry added but export failed" : "Failed to add entry"
				: "Failed to add entry",
		},
		onSuccess: () => { deps.refreshRecords(); onSuccess?.(); },
		onError: () => deps.refreshRecords(),
	});
}
